/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import {
  FormMode,
  GroupedFields,
  Layout,
  LayoutToFormValues,
  LayoutToSchema,
} from '@/components/forms/fields/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import {
  DefaultValues,
  FieldValues,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { z } from 'zod';
import { getMeta } from '../components/forms/field.schema';

function layoutToSchema<L extends Layout>(layout: L): LayoutToSchema<L> {
  // ???: z.object causes type erros
  // loose/strictObject both work, which is fine, its good to make concious choices
  // loosObject allows us to pass-through any extra fields, which we use
  // TODO: Explicitly type passed through fields
  return z.looseObject(Object.assign({}, ...Object.values(layout)));
}

interface UseEntityFormProps<
  TLayout extends Layout,
  TEntity extends LayoutToFormValues<TLayout> = LayoutToFormValues<TLayout>,
> {
  layout: TLayout;
  data?: TEntity;
  isLoading: boolean;
  defaultValues?: DefaultValues<LayoutToFormValues<TLayout>>;
}

export interface UseEntityFormReturn<TFormValues extends FieldValues> {
  form: UseFormReturn<TFormValues>;
  formMode: FormMode;
  setFormMode: (mode: Exclude<FormMode, 'loading'>) => void;
  groupedFields: GroupedFields;
}

export function useEntityForm<
  TLayout extends Layout,
  TEntity extends LayoutToFormValues<TLayout> = LayoutToFormValues<TLayout>,
>({
  layout,
  isLoading,
  data,
  defaultValues = {} as any, // ???: why is this needed?
}: UseEntityFormProps<TLayout, TEntity>): UseEntityFormReturn<
  LayoutToFormValues<TLayout>
> {
  const [formMode, setFormMode] = useState<FormMode>('loading');
  const schema = useMemo(() => layoutToSchema(layout), [layout]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: defaultValues as any, // ???: why is this needed?
    values: data,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  // Effect to sync form with query data
  useEffect(() => {
    if (isLoading) {
      setFormMode('loading');
    } else if (data) {
      form.reset(data);
      setFormMode('view');
    } else {
      // No query or no data, assume create mode
      setFormMode('edit');
    }
  }, [isLoading, data, form]);

  // Process the `layout` object into a renderable `groupedFields` array.
  // This is memoized to prevent reprocessing on every render.
  const groupedFields = useMemo<GroupedFields>(() => {
    return Object.entries(layout).reduce<GroupedFields>(
      (acc, [groupName, fields]) => {
        acc[groupName] = Object.entries(fields).map(
          ([fieldName, zodSchema]) => {
            // The meta property is attached to the schema's definition.
            // The module augmentation in `form.schemas.ts` makes this type-safe.
            const meta = getMeta(zodSchema);
            if (!meta) {
              throw new Error(
                `Field "${fieldName}" in group "${groupName}" is missing metadata. Did you forget to use the createField() helper?`,
              );
            }
            return {
              name: fieldName,
              isOptional: zodSchema.safeParse(undefined).success,
              meta,
            };
          },
        );
        return acc;
      },
      {},
    );
  }, [layout]);

  return {
    form,
    formMode,
    setFormMode,
    groupedFields,
  };
}
